import { useState } from "react";
import Snackbar from "../LoadingError/snackbar";
import "./landing.css";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // NOTE: /api/contact has no implementation and returns 404, so this form
    // currently always shows the failure message and the phone number.
    // The old handler was Next.js-shaped code in src/api/contact.js, which CRA
    // never mounts as a route; it was deleted because it carried the mailbox
    // password in plain text. Wiring this up needs a real serverless function
    // (a Vercel `api/` function at the repo root) or a route on the Express API.
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setFormData({ name: "", email: "", message: "" });
        setSuccess("Your message has been sent.");
        setShowSnackbar(true);
      } else {
        const data = await res.json();
        setError(data.message || "That did not send. Try again.");
      }
    } catch (error) {
      setError("That did not send. Try again, or call us on +961 71 144 332.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tfm-section tfm-section--paper" id="contactus">
      <div className="tfm-container tfm-contact__grid">
        <div>
          <p className="tfm-eyebrow">Contact us</p>
          <h2 className="tfm-h2" style={{ marginTop: "1.1rem" }}>
            Talk to someone who knows the floor.
          </h2>
          <p className="tfm-lede">
            Questions about the catalogue, a delivery, or setting your kitchen
            up on the app — reach us whichever way is quickest.
          </p>

          <div className="tfm-contact__list">
            <a className="tfm-contact__row" href="tel:+96171144332">
              <PhoneIcon />
              <span>+961 71 144 332</span>
            </a>
            <a
              className="tfm-contact__row"
              href="mailto:info@tripolifreshmarket.com"
            >
              <MailIcon />
              <span>info@tripolifreshmarket.com</span>
            </a>
            <p className="tfm-contact__row">
              <MapPinIcon />
              <span>Municipal Fresh Market, Tripoli, Lebanon</span>
            </p>
          </div>
        </div>

        <form className="tfm-form" onSubmit={handleSubmit}>
          <div className="tfm-field">
            <label htmlFor="name" className="tfm-mono">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </div>
          <div className="tfm-field">
            <label htmlFor="email" className="tfm-mono">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@yourbusiness.com"
              required
            />
          </div>
          <div className="tfm-field">
            <label htmlFor="message" className="tfm-mono">
              Message
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="What do you need?"
              rows={4}
              maxLength={1500}
              required
            />
          </div>

          <button
            type="submit"
            className="tfm-btn tfm-btn--green"
            disabled={loading}
          >
            {loading ? "Sending…" : "Send message"}
          </button>

          {error && <p className="tfm-form__msg tfm-form__msg--err">{error}</p>}
          {success && <p className="tfm-form__msg tfm-form__msg--ok">{success}</p>}
        </form>
      </div>

      {showSnackbar && (
        <Snackbar
          message="Your message has been sent."
          style={{ backgroundColor: "#4e8b3a" }}
        />
      )}
    </section>
  );
}

function MailIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
